import { type ForwardStyleProps, forwardStyle, styled } from '@glitz/react';
import * as React from 'react';
const slideInTimeInMs = 700;
const slideOutTimeInMs = 500;

function slideIn(element: HTMLDivElement) {
  element.style.transform = 'translateY(0%)';
  element.style.transition = `transform ${slideInTimeInMs}ms, opacity ${slideInTimeInMs}ms`;
  element.style.opacity = '1';
  element.style.pointerEvents = 'auto';
}

function slideOut(element: HTMLDivElement) {
  element.style.transform = 'translateY(-50%)';
  element.style.transition = `transform ${slideOutTimeInMs}ms, opacity ${slideOutTimeInMs}ms`;
  element.style.opacity = '0';
  element.style.pointerEvents = 'auto';
  delay(slideOutTimeInMs).then(() => reset(element));
}

function reset(element: HTMLDivElement) {
  element.style.transform = '';
  element.style.transition = '';
  element.style.opacity = '';
  element.style.pointerEvents = 'none';
}

function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function* carousel(elements: HTMLDivElement[]) {
  for (let i = 0; i >= 0; i++) {
    if (i === 0) {
      slideIn(elements[i]);
      yield;
    }
    slideOut(elements[i % elements.length]);
    slideIn(elements[(i + 1) % elements.length]);
    yield;
  }
}

function runCarousel(
  runSlideAnimations: Generator<undefined, void, unknown>,
  timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
) {
  if (!runSlideAnimations.next().done) {
    timeoutRef.current = setTimeout(() => runCarousel(runSlideAnimations, timeoutRef), 3000);
  }
}

type CarouselPropType = React.PropsWithChildren &
  ForwardStyleProps & {
    isAnimating?: boolean;
  };

const VerticalCarousel = styled(
  forwardStyle(({ isAnimating, compose, children }: CarouselPropType) => {
    const carouselRef = React.useRef<HTMLDivElement>(null);
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    React.useEffect(() => {
      const messages = carouselRef.current?.children as any;
      if (messages && messages.length > 0) {
        const carouselInstance = carousel(messages);
        runCarousel(carouselInstance, timeoutRef);
      }

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, [isAnimating]);

    return (
      <styled.Div css={compose()} ref={carouselRef}>
        {children}
      </styled.Div>
    );
  }),
);

export default VerticalCarousel;
